import { useAddToast } from '../../../contexts/ToastContext';
import { queryClient } from '../../../ReactQuery';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import { ISSUE_CATEGORY_SUMMARY_QUERY_KEY_PREFIX } from '../issues/useIssueCategorySummary';
import type { RevisionIssueScanResponse } from '../../../../shared/types/routes/revisions';

import { getQueryKey as getRevisionQueryKey } from './useRevision';

//  Function to kick off an issue scan for a given revision.
function scanRevision(revisionLocator: string, params?: { steady?: boolean }) {
  return get<RevisionIssueScanResponse>(`/api/revisions/${nuc(revisionLocator)}/issues`, false, params);
}

/**
 * Mutation which will kick off an issue scan for a given revision.
 * Automatically invalidates the revision query on success to refetch updated data.
 * Shows success/error toasts on completion.
 */
export default function useScanRevision() {
  const addToast = useAddToast();

  return useToastedMutation({
    mutationFn: scanRevision,
    errorToastMessage: 'Failed to scan for issues',
    onSuccess: async (_data, revisionLocator) => {
      // Invalidate revision and category summary queries so both refetch after scan completes.
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getRevisionQueryKey(revisionLocator) }),
          queryClient.invalidateQueries({ queryKey: ISSUE_CATEGORY_SUMMARY_QUERY_KEY_PREFIX }),
        ]);
        addToast({
          type: 'success',
          content: 'Issue scan completed successfully',
        });
      } catch {
        addToast({
          type: 'error',
          content: 'Issue scan completed, but refreshing the page data failed. Please reload to see the latest results.',
        });
      }
    },
  });
}
